import { IonAlert, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonContent, IonFooter, IonGrid, IonHeader, IonIcon, IonModal, IonPage, IonRow, IonTextarea, IonTitle, IonToolbar } from "@ionic/react"
import { RouteComponentProps } from "react-router-dom"
import { BackButtonRow, PageContainer, QuillDisplayer, ReportDayCard, RoundedButton, WeekSwitcher } from "../../components"
import { ReportDay, WeekSwitcherProps, EntryCommentItem } from "../../models/reportModels"
import { useEffect, useState } from "react"
import UserSelect from "../../components/admin/userSelect/UserSelect"
import { alertCircleOutline, checkmarkCircleOutline, chatboxOutline, closeOutline } from "ionicons/icons"
import { format } from "date-fns"
import { de } from "date-fns/locale"
import { calcTotalTime } from "../../util/report-helper"
import { isJsonString } from "../../util/helper"
import ReportService from "../../data/services/ReportService"
import { useAppDispatch, useAppSelector } from "../../data/redux/hooks"
import { setReportWeek, updateReportWeekState } from "../../data/redux/reportWeekSlice"
import { changeReportListWeekState } from "../../data/redux/reportListSlice"
import { getTrainees } from "../../data/redux/traineeListSlice"
import { getReportStats } from "../../data/redux/reportStatsSlice"
import { User } from "../../models/userModels"
import { setError } from "../../data/redux/errorSlice"
import { error500Text } from "../../util/error-helper"
import RecordBookWeek from "../recordBook/RecordBookWeek"
import ReportWeekAttachments from "../recordBook/ReportWeekAttachments"

interface TraineeReportDetailProps extends RouteComponentProps<{
    traineeId: string,
    year: string,
    week: string,
}> { }


interface Params {
    traineeId: string,
    year: string | number,
    week: string | number
}



const TraineeReportDetailContent: React.FC<TraineeReportDetailProps> = ({ match }) => {
    const [params, setParams] = useState<Params>(match.params)
    const [acceptAlertOpen, setAcceptAlertOpen] = useState(false);
    const [dismissAlertOpen, setDismissAlertOpen] = useState(false);
    const [commentModalOpen, setCommentModalOpen] = useState(false);
    const [commentByKey, setCommentByKey] = useState<Record<string, string>>({});
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [adminDismissAlertOpen, setAdminDismissAlertOpen] = useState(false);
    const dispatch = useAppDispatch();
    const reportWeek = useAppSelector((state) => state.reportWeek.reportWeek);
    const { user } = useAppSelector((state) => state.user);
    const { traineesWithOpenEntries: userList } = useAppSelector((state) => state.traineeList)
    const [selectedTrainee, setSelectedTrainee] = useState<User | undefined>();


    useEffect(() => {
        dispatch(getTrainees(undefined))
    }, [dispatch])

    useEffect(() => {
        const findSelectedTrainee = () => {
            var result = userList.find(trainee => {
                return trainee.id === Number(params.traineeId)
            })
            setSelectedTrainee(result)
        }
        findSelectedTrainee()
    }, [userList, params.traineeId])

    useEffect(() => {
        let ignore = false;

        const setCurrentReportWeek = async () => {
            try {
                const res = await ReportService.getReportWeekEntries(params.traineeId, params.year, params.week)
                if (!ignore) {
                    if (res && res.report) {
                        dispatch(setReportWeek(res.report))
                    } else if (res.error) {
                        dispatch(setError({
                            isOpen: true,
                            message: res.error
                        }))
                        dispatch(setReportWeek(undefined))
                    }
                }
            } catch (err) {
                dispatch(setError({
                    isOpen: true,
                    message: error500Text
                }))
                console.log(err)
            }

        }

        if (params.year && params.week && params.traineeId) {
            setCurrentReportWeek();
        }

        return () => {
            ignore = true;
        }
    }, [params, dispatch])

    const userSelectCallback = (id: string) => {
        setParams({
            ...params,
            traineeId: id
        })
        window.history.replaceState(null, "Meine Azubis", `/tabs/azubis/${id}/${params.year}/${params.week}`)
    }

    const weekSwitcherCallback = (year: string | number, week: string | number) => {
        setParams({
            ...params,
            year: year,
            week: week
        })
        window.history.replaceState(null, "Berichtsheft", `/tabs/azubis/${params.traineeId}/${year}/${week}`)
    }

    const getTrainee = (id: string) => {
        const trainee = userList.find(x => String(x.id) === String(id))
        return trainee?.trainee_start
    }

    const weekSwitcherProps: WeekSwitcherProps = {
        year: params.year,
        week: params.week,
        changeWeek: weekSwitcherCallback,
        state: reportWeek && reportWeek.state,
        trainingStart: getTrainee(params.traineeId) || undefined
    }

    const acceptCalendarWeek = async () => {
        if (reportWeek && reportWeek.id) {
            const res = await ReportService.setReportWeekState(reportWeek.id, "completed")
            //console.log(res)
            if (res.report) {
                dispatch(updateReportWeekState({ state: res.report.state, updated_at: res.report.updated_at }))
                dispatch(changeReportListWeekState({ state: res.report.state, updated_at: res.report.updated_at, id: res.report.id }))
                dispatch(getReportStats())
                dispatch(getTrainees(undefined))
            }
        }
    }

    const dismissCalendarWeek = async (e: any) => {
        //console.log(e[0])
        if (reportWeek && reportWeek.id) {
            const res = await ReportService.setReportWeekState(reportWeek.id, "dismissed", e[0])
            if (res.report) {
                dispatch(updateReportWeekState({ state: res.report.state, updated_at: res.report.updated_at }))
                dispatch(changeReportListWeekState({ state: res.report.state, updated_at: res.report.updated_at, id: res.report.id }))
                dispatch(getReportStats())
                dispatch(getTrainees(undefined))
            }
        }
    }

    const getStateButton = (state: string) => {
        switch (state) {
            case "submitted":
                return (
                    <>
                        <RoundedButton type="button" color="secondary" onClick={() => setCommentModalOpen(true)}>
                            KW kommentieren und ablehnen
                            <IonIcon icon={chatboxOutline} />
                        </RoundedButton>
                        <RoundedButton type="button" color="danger" onClick={() => setDismissAlertOpen(true)}>
                            KW ablehnen
                            <IonIcon icon={alertCircleOutline} />
                        </RoundedButton>
                        <RoundedButton type="button" color="primary" onClick={() => setAcceptAlertOpen(true)}>
                            KW signieren
                            <IonIcon icon={checkmarkCircleOutline} />
                        </RoundedButton>
                    </>
                )
            case "completed":
                return (
                    <>
                        <RoundedButton type="button" color="success" fill="outline" disabled>
                            KW{params.week} am {format(new Date(reportWeek.updated_at), "dd.MM.yy")} signiert
                            <IonIcon icon={checkmarkCircleOutline} />
                        </RoundedButton>
                        {user?.role === "company_admin" && (
                            <RoundedButton type="button" color="danger" onClick={() => setAdminDismissAlertOpen(true)}>
                                Bericht ablehnen
                                <IonIcon icon={alertCircleOutline} />
                            </RoundedButton>
                        )}
                    </>
                )
            default: return null
        }
    }

    const commentKey = (type: "entry" | "field", idOrField: number | string) =>
        `${type}-${idOrField}`;

    const handleCommentChange = (key: string, value: string) => {
        setCommentByKey((prev) => ({ ...prev, [key]: value ?? "" }));
    };

    const handleSubmitComments = async () => {
        if (!reportWeek?.id || !user?.id) return;
        const traineeIdNum = Number(params.traineeId);
        if (!Number.isFinite(traineeIdNum)) return;

        const comments: EntryCommentItem[] = [];
        if (selectedTrainee?.report_type === "daily") {
            reportWeek.days.forEach((day: ReportDay) => {
                const reportDayId = day.id ?? null;
                day.entries.forEach((entry) => {
                    const key = commentKey("entry", entry.id);
                    const text = (commentByKey[key] ?? "").trim();
                    if (text && reportDayId != null)
                        comments.push({
                            report_day_id: reportDayId,
                            report_entry_id: entry.id,
                            comment: text,
                        });
                });
            });
        } else if (selectedTrainee?.report_type === "weekly") {
            ["company_activities", "school_activities", "other_activities"].forEach((field) => {
                const key = commentKey("field", field);
                const text = (commentByKey[key] ?? "").trim();
                if (text) comments.push({ field, comment: text });
            });
        }
        if (comments.length === 0) {
            dispatch(setError({ isOpen: true, message: "Bitte mindestens ein Kommentarfeld ausfüllen." }));
            return;
        }
        setCommentSubmitting(true);
        try {
            const res = await ReportService.submitEntryComments({
                trainer_id: user.id,
                trainee_id: traineeIdNum,
                report_week_id: reportWeek.id,
                comments,
            });
            if (res?.error) {
                dispatch(setError({ isOpen: true, message: res.error }));
                return;
            }
            setCommentByKey({});
            setCommentModalOpen(false);
            const stateRes = await ReportService.setReportWeekState(reportWeek.id, "dismissed");
            if (stateRes?.report) {
                dispatch(updateReportWeekState({ state: stateRes.report.state, updated_at: stateRes.report.updated_at }));
                dispatch(changeReportListWeekState({ state: stateRes.report.state, updated_at: stateRes.report.updated_at, id: stateRes.report.id }));
                dispatch(getReportStats());
                dispatch(getTrainees(undefined));
            }
        } catch (err) {
            dispatch(setError({ isOpen: true, message: error500Text }));
        } finally {
            setCommentSubmitting(false);
        }
    };

    const renderCommentModalContent = () => {
        if (!reportWeek) return null
        if (selectedTrainee?.report_type === "weekly") {
            const blocks: { key: string; title: string; value: string | null }[] = [
                { key: "company_activities", title: "Betriebliche Tätigkeiten", value: reportWeek.company_activities },
                { key: "school_activities", title: "Berufsschule (Unterrichtsthemen)", value: reportWeek.school_activities },
                { key: "other_activities", title: "Unterweisungen, Lehrgespräche, betrieblicher Unterricht, außerbetriebliche Schulungsveranstaltungen, Zwischenprüfung", value: reportWeek.other_activities },
            ]
            return (
                <>
                    {blocks.map(({ key, title, value }) => {
                        const fieldKey = commentKey("field", key);
                        return (
                            <IonCard key={key} className="reportCard weekCard">
                                <IonCardHeader>
                                    <IonCardTitle>{title}</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    <div className="weeklyEntryContainer">
                                        {value ? (
                                            <QuillDisplayer defaultValue={JSON.parse(value)} />
                                        ) : (
                                            <i>Noch nichts eingetragen</i>
                                        )}
                                    </div>
                                    <IonTextarea
                                        placeholder="Kommentar des Ausbilders …"
                                        rows={4}
                                        className="ion-margin-top"
                                        value={commentByKey[fieldKey] ?? ""}
                                        onIonInput={(e) => handleCommentChange(fieldKey, e.detail.value ?? "")}
                                    />
                                </IonCardContent>
                            </IonCard>
                        );
                    })}
                </>
            )
        }
        if (selectedTrainee?.report_type === "daily") {
            return (
                <>
                    {reportWeek.days.map((day: ReportDay) => (
                        <IonCard key={day.id ?? day.date} className="reportCard">
                            <IonCardHeader>
                                <IonCardTitle>
                                    {day.date && format(new Date(day.date), "eeee, dd.MM.yyyy", { locale: de })}
                                </IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div className="entryContainer">
                                    {day.entries.map((entry) => {
                                        const entryKey = commentKey("entry", entry.id);
                                        const entryContent = isJsonString(entry.activity) ? (
                                            <div key={entry.id} className="entry entryBorder">
                                                <div className="entryDescription">
                                                    <QuillDisplayer
                                                        key={entry.activity}
                                                        defaultValue={JSON.parse(entry.activity)}
                                                    />
                                                </div>
                                                <div>{entry.duration.substring(0, 5)}</div>
                                            </div>
                                        ) : (
                                            <div key={entry.id} className="entry entryBorder">
                                                <div className="entryDescription">{entry.activity}</div>
                                                <div>{entry.duration.substring(0, 5)}</div>
                                            </div>
                                        )
                                        return (
                                            <div key={entry.id} className="entryWithComment">
                                                {entryContent}
                                                <IonTextarea
                                                    placeholder="Kommentar zu diesem Eintrag …"
                                                    rows={2}
                                                    className="ion-margin-top ion-margin-bottom"
                                                    value={commentByKey[entryKey] ?? ""}
                                                    onIonInput={(e) => handleCommentChange(entryKey, e.detail.value ?? "")}
                                                />
                                            </div>
                                        )
                                    })}
                                    <div className="entry">
                                        <div><b>Gesamt</b></div>
                                        <div><b>{new Date(calcTotalTime(day) * 1000).toISOString().substring(11, 16)}</b></div>
                                    </div>
                                </div>
                            </IonCardContent>
                        </IonCard>
                    ))}
                </>
            )
        }
        return null
    }

    const dismissCalendarWeekByAdmin = async (e: any) => {
        if (!reportWeek || !reportWeek.id) return;
        const comment = (e && e[0] ? String(e[0]).trim() : "");
        if (!comment) {
            dispatch(setError({ isOpen: true, message: "Bitte geben Sie einen Kommentar ein." }));
            return;
        }
        const res = await ReportService.setReportWeekState(reportWeek.id, "dismissed", comment);
        if (res.report) {
            dispatch(updateReportWeekState({ state: res.report.state, updated_at: res.report.updated_at }));
            dispatch(changeReportListWeekState({ state: res.report.state, updated_at: res.report.updated_at, id: res.report.id }));
            dispatch(getReportStats());
            dispatch(getTrainees(undefined));
        }
    }

    return (
        <IonPage>
            <PageContainer
                name="Meine Azubis"
                backButton={true}
                weekSwitcherToolbar={true}
                weekSwitcherProps={weekSwitcherProps}
            >
                <IonGrid>
                    <BackButtonRow path={`/tabs/azubis/${params.traineeId}`} />
                    <IonRow>
                        <IonCol size="12" >
                            <IonCard>
                                <IonCardHeader>
                                    <div className="reportWeekDesktopHeader">
                                        <div className="minWidthCol ion-hide-lg-down">
                                            <IonCardTitle className='headline'>Meine Azubis</IonCardTitle>
                                        </div>
                                        <div className="ion-hide-lg-down">
                                            <WeekSwitcher week={params.week} year={params.year} changeWeek={weekSwitcherCallback} state={reportWeek && reportWeek.state} trainingStart={getTrainee(params.traineeId) || undefined} />
                                        </div>
                                        <div className="minWidthCol">
                                            {userList && <UserSelect selectedUserId={params.traineeId} setSelectedUserId={userSelectCallback} userList={userList} />}
                                        </div>
                                    </div>
                                </IonCardHeader>
                            </IonCard>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        {selectedTrainee && selectedTrainee.report_type === "daily" && <IonCol>
                            {reportWeek &&
                                <>
                                    {reportWeek.state === "submitted" || reportWeek.state === "completed" ?
                                        reportWeek.days.map((day: ReportDay) => {
                                            return (
                                                <IonCol size="12" key={day.id}>
                                                    <ReportDayCard day={day} />
                                                </IonCol>
                                            )
                                        }
                                        )
                                        :
                                        <IonCol size="12">
                                            <IonCard>
                                                <IonCardContent>
                                                    {reportWeek.state === "open" && "Ihr Azubi hat den Wochen-Bericht noch nicht für Sie freigegeben."}
                                                    {reportWeek.state === "dismissed" && "Sie haben den Bericht abgelehnt. Ihr Azubi hat jetzt Zeit den Bericht noch mal zu bearbeiten und gibt ihn dann erneut für Sie frei."}
                                                </IonCardContent>
                                            </IonCard>
                                        </IonCol>
                                    }
                                </>

                            }
                        </IonCol>}

                        {selectedTrainee && selectedTrainee.report_type === "weekly" &&
                            <>
                                {reportWeek &&
                                    <>
                                        {reportWeek.state === "submitted" || reportWeek.state === "completed" ?
                                            <RecordBookWeek reportWeek={reportWeek} editable={false} />
                                            :
                                            <IonCol size="12">
                                                <IonCard>
                                                    <IonCardContent>
                                                        {reportWeek.state === "open" && "Ihr Azubi hat den Wochen-Bericht noch nicht für Sie freigegeben."}
                                                        {reportWeek.state === "dismissed" && "Sie haben den Bericht abgelehnt. Ihr Azubi hat jetzt Zeit den Bericht noch mal zu bearbeiten und gibt ihn dann erneut für Sie frei."}
                                                    </IonCardContent>
                                                </IonCard>
                                            </IonCol>
                                        }
                                    </>

                                }

                            </>
                        }
                    </IonRow>
                    <IonRow>
                        {reportWeek &&
                            <IonCol>
                                <div className="reportDetailBtnContainer">
                                    {getStateButton(reportWeek.state)}
                                </div>
                            </IonCol>
                        }
                    </IonRow>
                    {reportWeek && (
                        <IonRow>
                            <ReportWeekAttachments
                                reportWeek={reportWeek}
                                editable={false}
                                userId={Number(params.traineeId)}
                                year={params.year}
                                week={params.week}
                            />
                        </IonRow>
                    )}
                </IonGrid>
            </PageContainer>

            <IonModal
                className="commentModal"
                isOpen={commentModalOpen}
                onDidDismiss={() => {
                    setCommentModalOpen(false);
                    setCommentByKey({});
                }}
            >
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>KW ausführlich kommentieren</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={() => setCommentModalOpen(false)}>
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    {renderCommentModalContent()}
                </IonContent>
                <IonFooter>
                    <IonToolbar>
                        <div className="ion-padding">
                            <IonButton
                                expand="block"
                                onClick={handleSubmitComments}
                                disabled={commentSubmitting}
                            >
                                {commentSubmitting ? "Wird gesendet …" : "Kommentare absenden"}
                            </IonButton>
                        </div>
                    </IonToolbar>
                </IonFooter>
            </IonModal>

            <IonAlert
                isOpen={acceptAlertOpen}
                onDidDismiss={() => setAcceptAlertOpen(false)}
                header={`KW${params.week} akzeptieren`}
                message="Möchten Sie diese Kalenderwoche akzeptieren? Der Vorgang kann nicht rückgängig gemacht werden."
                buttons={[
                    {
                        text: 'abbrechen',
                        role: 'cancel',
                        handler: () => setAcceptAlertOpen(false)
                    },
                    {
                        text: "akzeptieren",
                        role: 'confirm',
                        handler: acceptCalendarWeek
                    }
                ]}
            />

            <IonAlert
                isOpen={dismissAlertOpen}
                onDidDismiss={() => setDismissAlertOpen(false)}
                header={`KW${params.week} ablehnen`}
                inputs={[
                    {
                        type: "textarea",
                        placeholder: "Kommentar"
                    }
                ]}
                buttons={[
                    {
                        text: 'abbrechen',
                        role: 'cancel',
                        handler: () => setDismissAlertOpen(false)
                    },
                    {
                        text: "ablehnen",
                        role: 'confirm',
                        handler: dismissCalendarWeek
                    }
                ]}
            />

            <IonAlert
                isOpen={adminDismissAlertOpen}
                onDidDismiss={() => setAdminDismissAlertOpen(false)}
                header={`KW${params.week} ablehnen (Personalleiter)`}
                inputs={[
                    {
                        type: "textarea",
                        placeholder: "Kommentar für Azubi und Ausbilder"
                    }
                ]}
                buttons={[
                    {
                        text: 'abbrechen',
                        role: 'cancel',
                        handler: () => setAdminDismissAlertOpen(false)
                    },
                    {
                        text: "ablehnen",
                        role: 'confirm',
                        handler: dismissCalendarWeekByAdmin
                    }
                ]}
            />
        </IonPage>
    )
}

const TraineeReportDetail: React.FC<TraineeReportDetailProps> = (props: TraineeReportDetailProps) => {
    return (
        <TraineeReportDetailContent
            {...props}
            key={props.match.params.year + props.match.params.week + props.match.params.traineeId}
        />
    )
}

export default TraineeReportDetail;